/**
 * Wallet Remapper
 * Flutter: lib/data/models/wallet.dart
 */
import type { JsonObject } from '@/data/types/json';
import type { WalletDto } from '@/data/dtos/wallet/walletDto';
import type { WalletStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { WalletEntity } from '@/domain/entities/wallet/walletEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class WalletRemapper {
  /**
   * Parse raw Firestore data into a WalletDto.
   *
   * Mirrors Flutter's `Wallet.fromJson` (lib/data/models/wallet.dart) whose
   * `late double coins / diamonds / updatedTimestamp` fields throw when
   * absent. Returns `null` and logs via `appLogger.error` on malformed input
   * — a defaulted `{ coins: 0, ... }` would risk overwriting a good local
   * copy with a zeroed wallet, so callers receive `null` and skip the write.
   */
  static fromJson(data: JsonObject): WalletDto | null {
    if (
      !data ||
      typeof data.coins !== 'number' ||
      typeof data.diamonds !== 'number' ||
      typeof data.updatedTimestamp !== 'number'
    ) {
      const keys = data && typeof data === 'object' ? Object.keys(data).join(',') : String(data);
      appLogger.error(
        'Malformed wallet snapshot — required fields missing or wrong type',
        'WalletRemapper.fromJson',
        { context: { keys } },
      );
      return null;
    }
    return {
      coins: data.coins,
      diamonds: data.diamonds,
      updatedTimestamp: data.updatedTimestamp,
      lastWithdrawTimestamp: data.lastWithdrawTimestamp as number | undefined,
      paymentId: data.paymentId as string | undefined,
      paymentAccountStatus: data.paymentAccountStatus as number | undefined,
      paymentProcessor: data.paymentProcessor as number | undefined,
      currency: data.currency as string | undefined,
    };
  }

  static toStorage(dto: WalletDto): WalletStorage {
    return {
      coins: dto.coins,
      diamonds: dto.diamonds,
      updatedTimestamp: dto.updatedTimestamp,
      lastWithdrawTimestamp: dto.lastWithdrawTimestamp,
      paymentId: dto.paymentId,
      paymentAccountStatus: dto.paymentAccountStatus,
      paymentProcessor: dto.paymentProcessor,
      currency: dto.currency,
    };
  }

  static toEntity(storage: WalletStorage): WalletEntity {
    return {
      coins: storage.coins,
      diamonds: storage.diamonds,
      updatedTimestamp: storage.updatedTimestamp,
      lastWithdrawTimestamp: storage.lastWithdrawTimestamp,
      paymentId: storage.paymentId,
      paymentAccountStatus: storage.paymentAccountStatus,
      paymentProcessor: storage.paymentProcessor,
      currency: storage.currency,
    };
  }
}
